import React, { useEffect, useState } from 'react';
import type { ConnectionStatus } from '../contexts/GameContext';
import { useSession } from '../lib/auth-client';

interface JoinModalProps {
  isOpen: boolean;
  onJoin: (name: string) => void;
  connectionStatus: ConnectionStatus;
}

const JoinModal: React.FC<JoinModalProps> = ({ isOpen, onJoin, connectionStatus }) => {
  const [name, setName] = useState('');
  const [hasEditedName, setHasEditedName] = useState(false);
  const { data: session } = useSession();

  useEffect(() => {
    const displayName = session?.user?.name?.trim();
    if (displayName && name === '' && !hasEditedName) {
      setName(displayName);
    }
  }, [session?.user?.name, name, hasEditedName]);

  useEffect(() => {
    if (isOpen) {
      setHasEditedName(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) {
      onJoin(trimmed);
      setName('');
    }
  };

  const isConnecting = connectionStatus === 'connecting';
  const isDisconnected = connectionStatus === 'disconnected';
  const connectionMessage =
    connectionStatus === 'connected'
      ? null
      : connectionStatus === 'connecting'
        ? 'Connecting to the game…'
        : 'Unable to connect. Please check if the backend is running.';

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal">
        <h2>Join the planning table</h2>
        <p className="modal__helper">Enter your name so your teammates know who you are.</p>
        <form onSubmit={handleSubmit} className="modal__form">
          <label htmlFor="player-name" className="modal__label">
            Your name
          </label>
          <input
            id="player-name"
            type="text"
            value={name}
            onChange={(event) => {
              setHasEditedName(true);
              setName(event.target.value);
            }}
            placeholder="e.g. Alex"
            className="modal__input"
            autoFocus
            required
          />
          <button
            type="submit"
            className="modal__button"
            disabled={isConnecting || isDisconnected}
            data-foley-press="press"
          >
            {isConnecting ? 'Connecting…' : isDisconnected ? 'Waiting for connection…' : 'Join game'}
          </button>
          {connectionMessage && (
            <p className="modal__status" style={{ color: isDisconnected ? '#ff6b6b' : undefined }}>
              {connectionMessage}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default JoinModal;
